/**
 * analytics-shared/api/exportsApi.js
 *
 * Export dispatch. After wedge 5 all three formats (CSV / Excel / PDF)
 * are rendered server-side by the analytics_engine; the client just
 * issues a POST, picks up the binary, and saves it via the standard
 * a-tag-download dance.
 *
 * Backend endpoint:
 *   POST /v1/reports/:id/export?format=csv|excel|pdf
 *   → bytes + Content-Disposition: attachment; filename="<name>.<ext>"
 *
 * The export endpoint requires READ visibility on the report (gated by
 * the wedge-3 sharing service). For un-saved configs the caller should
 * save first — preview-as-PDF on an unsaved draft would need a
 * different shape and we don't have a UI for it today.
 */
import { analyticsClient } from './client';

const FORMAT_MIME = {
  csv: 'text/csv;charset=utf-8;',
  excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
};
const FORMAT_EXT = { csv: 'csv', excel: 'xlsx', pdf: 'pdf' };

const safeFilename = (name) =>
  (name || 'report').replace(/[^a-z0-9]+/gi, '_').toLowerCase();

function trigger(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function downloadExport(report, format) {
  if (!report?.id) {
    throw new Error('Save the report before exporting.');
  }
  if (!FORMAT_MIME[format]) {
    throw new Error(`Unknown export format: ${format}`);
  }
  const res = await analyticsClient.post(
    `/reports/${encodeURIComponent(report.id)}/export`,
    null,
    { params: { format }, responseType: 'blob' },
  );
  // Server sets the filename in Content-Disposition; we re-derive from
  // the report name so a renamed-but-not-yet-saved tab still produces a
  // sensible local filename.
  const blob = new Blob([res.data], { type: FORMAT_MIME[format] });
  trigger(`${safeFilename(report.name)}.${FORMAT_EXT[format]}`, blob);
  return blob.size;
}

export const exportsApi = {
  /**
   * Export the saved report as CSV. Returns the byte size of the
   * downloaded payload (useful for "Exported 12 KB" toasts).
   */
  exportReportCsv: async (config) => downloadExport(config, 'csv'),

  /** Export as Excel (.xlsx). */
  exportReportExcel: async (config) => downloadExport(config, 'excel'),

  /** Export as PDF. */
  exportReportPdf: async (config) => downloadExport(config, 'pdf'),

  /**
   * Format-parametric variant — call sites that pick the format
   * dynamically (e.g. a dropdown in the toolbar) use this directly.
   */
  exportReport: async (config, format = 'csv') => downloadExport(config, format),

  // Future async dispatcher placeholders — when reports get big enough
  // that synchronous download stops being viable, these become real.
  requestExport: async () => ({ job_id: null, status: 'unsupported' }),
  pollExportJob: async () => ({ status: 'unsupported' }),
};
